import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useController, useFormContext } from 'react-hook-form';
import { getBackgroundColor, getTextColor } from './utils';

const ScrollableIframe = ({ src, name, iframeTitle }) => {
  const { control } = useFormContext();
  const {
    field: { value: viewed, onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: false,
    rules: { required: true },
  });
  const iframeContainer = useRef(null);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const iframe = iframeContainer.current;

    if (iframe) {
      const handleScroll = () => {
        const doc = iframe.contentDocument;
        const scrollable = doc.scrollingElement;

        // Round scrollTop up
        // because Android devices return a float
        // that is slightly smaller than the true value
        const scrollPosition = Math.ceil(scrollable.scrollTop) + scrollable.clientHeight;
        if (scrollPosition >= scrollable.scrollHeight) {
          onChange(true);
        }
      };

      iframe.contentWindow.addEventListener('scroll', handleScroll);

      // Cleanup the event listener on component unmount
      return () => iframe.contentWindow?.removeEventListener('scroll', handleScroll);
    }
  }, [iframeContainer, onChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <Box
        component="iframe"
        src={src}
        width="100%"
        title={iframeTitle}
        data-testid="scrollable-iframe"
        name="scrollable-iframe"
        ref={iframeContainer}
        sx={(theme) => ({
          overflowY: 'auto',
          display: 'block',
          borderTopLeftRadius: theme.spacing(2),
          borderTopRightRadius: theme.spacing(2),
          border: '1px solid',
          borderColor: error ? 'error.main' : '#0000003B',
          backgroundColor: '#FAFAFA',
          minHeight: '300px',
          flex: 1,
        })}
      />
      <Typography
        variant="h4"
        sx={(theme) => ({
          backgroundColor: getBackgroundColor(viewed, !!error),
          color: getTextColor(viewed, !!error),
          borderBottomLeftRadius: theme.spacing(2),
          borderBottomRightRadius: theme.spacing(2),
          border: '1px solid #0000003B',
          borderTop: 'none',
          padding: 1.5,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        })}
      >
        {viewed && <CheckIcon sx={{ mr: 1 }} />}
        {viewed ? 'Fully Viewed' : 'Scroll to the Bottom to Continue'}
      </Typography>
    </Box>
  );
};

export default ScrollableIframe;
